import React, { useEffect, useState } from "react";
import translationText from "../../translation.js";
import RenderLocale from "../../common/component/fragments/RenderLocale";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import Page from "../../common/component/fragments/Page.jsx";

/* ---------------- STAR RATING COMPONENT ---------------- */

const StarRating = ({ name, value, onChange }) => {
  return (
    <div style={{ display: "flex", gap: "10px" }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          style={{
            fontSize: "1.8rem",
            cursor: "pointer",
            color: star <= value ? "#FAA652" : "#ccc",
            transition: "0.2s",
          }}
          onClick={() =>
            onChange({ target: { name, value: star } })
          }
          role="button"
          aria-label={`${star} star`}
        >
          ★
        </span>
      ))}
    </div>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */

const PassportGoogleSheet = () => {
  const [secureNumber, setSecureNumber] = useState("");
  const [policeStations, setPoliceStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    Yourfullname: "",
    Yourmobilenumber: "",
    YouremailID: "",
    Alphanumericpassport: "",
    courtesy_rating: 0,
    issues_faced: "",
    time_taken_ps: "",
    time_taken_cp: "",
    overall_rating: 0,
    police_station: "",
    suggestions: "",
  });

  /* -------- Fetch secure number + police stations -------- */
  useEffect(() => {
    fetch("https://admin.punepolice.gov.in/api/get-secure-number")
      .then((res) => res.json())
      .then((data) => {
        setSecureNumber(data.secure_number);
        setPoliceStations(data.police_stations || []);
        setLoading(false);
      })
      .catch(() => {
        alert("Failed to load secure number / police stations");
        setLoading(false);
      });
  }, []);

  /* -------- Handle change -------- */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* -------- Submit form -------- */
  const submitForm = (e) => {
    e.preventDefault();

    fetch("https://admin.punepolice.gov.in/api/passport-feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        securenumber: secureNumber,
        ...form,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setSubmitted(true); // ✅ CLOSE FORM
        } else {
          alert(data.error || "Submission failed");
        }
      })
      .catch(() => alert("Server error"));
  };

  if (loading) return <p>Loading form…</p>;

  return (
    <Page pageTitle={translationText.PassPortVerification}>
      <Container className="my-4 form-wrapper lost-and-found">
        <Row>
          <Col xs={12}>
            <RenderLocale keyName="PassPortVerificationIntro" /><br />
            <RenderLocale keyName="PassPortVerificationIntro1" /><br />
            <RenderLocale keyName="PassPortVerificationIntro2" /><br />
            <RenderLocale keyName="PassPortVerificationIntro3" /><br /><br />
          </Col>

          <Col xs={12}>
            {submitted ? (
              /* ---------- SUCCESS MESSAGE ---------- */
              <Alert variant="success">
                <h4>
                  <RenderLocale keyName="PassPortVerificationSuccess" />
                </h4>
                <p>
                  Thank you for your feedback. Your response has been recorded
                  successfully.
                </p>
              </Alert>
            ) : (
              /* ---------- FORM ---------- */
              <Form noValidate onSubmit={submitForm}>

                {/* Full Name + Mobile */}
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <RenderLocale keyName="PassPortVerificationQ1" />
                      </Form.Label>
                      <Form.Control
                        name="Yourfullname"
                        value={form.Yourfullname}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <RenderLocale keyName="PassPortVerificationQ2" />
                      </Form.Label>
                      <Form.Control
                        name="Yourmobilenumber"
                        value={form.Yourmobilenumber}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Email + Passport */}
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <RenderLocale keyName="PassPortVerificationQ3" />
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="YouremailID"
                        value={form.YouremailID}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <RenderLocale keyName="PassPortVerificationQ4" />
                      </Form.Label>
                      <Form.Control
                        name="Alphanumericpassport"
                        value={form.Alphanumericpassport}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Courtesy Rating */}
                <Form.Group>
                  <Form.Label>
                    <RenderLocale keyName="PassPortVerificationQ5" />
                  </Form.Label>
                  <StarRating
                    name="courtesy_rating"
                    value={form.courtesy_rating}
                    onChange={handleChange}
                  />
                </Form.Group>

                {/* Issues Faced */}
                <Form.Group>
                  <Form.Label>
                    <RenderLocale keyName="PassPortVerificationQ6" />
                  </Form.Label>
                  <Row>
                    <Col md={2}>
                      <Form.Check
                        type="radio"
                        name="issues_faced"
                        value="Yes"
                        label={<RenderLocale keyName="PassPortVerificationOptionYes" />}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Check
                        type="radio"
                        name="issues_faced"
                        value="No"
                        label={<RenderLocale keyName="PassPortVerificationOptionNo" />}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                </Form.Group>

                {/* Time at PS */}
                <Form.Group>
                  <Form.Label>
                    <RenderLocale keyName="PassPortVerificationQ7" />
                  </Form.Label>
                  <Row>
                    <Col md={4}>
                      <Form.Check
                        type="radio"
                        name="time_taken_ps"
                        value="Less than 7 days"
                        label={<RenderLocale keyName="PassPortVerificationQ7Option1" />}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="radio"
                        name="time_taken_ps"
                        value="7 to 15 days"
                        label={<RenderLocale keyName="PassPortVerificationQ7Option2" />}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="radio"
                        name="time_taken_ps"
                        value="More than 15 days"
                        label={<RenderLocale keyName="PassPortVerificationQ7Option3" />}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                </Form.Group>

                {/* Time at CP */}
                <Form.Group>
                  <Form.Label>
                    <RenderLocale keyName="PassPortVerificationQ8" />
                  </Form.Label>
                  <Row>
                    <Col md={4}>
                      <Form.Check
                        type="radio"
                        name="time_taken_cp"
                        value="Less than 4 days"
                        label={<RenderLocale keyName="PassPortVerificationQ8Option1" />}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="radio"
                        name="time_taken_cp"
                        value="4 to 8 days"
                        label={<RenderLocale keyName="PassPortVerificationQ8Option2" />}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="radio"
                        name="time_taken_cp"
                        value="More than 8 days"
                        label={<RenderLocale keyName="PassPortVerificationQ8Option3" />}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                </Form.Group>

                {/* Overall Rating */}
                <Form.Group>
                  <Form.Label>
                    <RenderLocale keyName="PassPortVerificationQ9" />
                  </Form.Label>
                  <StarRating
                    name="overall_rating"
                    value={form.overall_rating}
                    onChange={handleChange}
                  />
                </Form.Group>

                {/* Police Station */}
                <Form.Group>
                  <Form.Label>
                    <RenderLocale keyName="PassPortVerificationQ10" />
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="police_station"
                    value={form.police_station}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {policeStations.map((ps) => (
                      <option key={ps.id} value={ps.name}>
                        {ps.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                {/* Suggestions */}
                <Form.Group>
                  <Form.Label>
                    <RenderLocale keyName="PassPortVerificationQ11" />
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="suggestions"
                    value={form.suggestions}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Button type="submit" className="mt-3">
                  Submit Feedback
                </Button>

              </Form>
            )}
          </Col>
        </Row>
      </Container>
    </Page>
  );
};

export default PassportGoogleSheet;
